"use client";

import React from "react";
import { Building2, House as Home, ShieldCheck, ArrowRight, HardHat, Umbrella, CircleCheck as CheckCircle2, MapPin } from 'lucide-react';
import { motion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const coverageAreas = ["HP9 principal district (Old & New Town)", "Town-centre commercial properties", "Office & retail developments", "Residential & estate properties", "Roof replacements & repairs"];
const residentialFeatures = ["Roof replacements and emergency repairs", "Single and multi-storey extensions", "Chimney stack repairs and repointing", "External rendering and timber cladding", "Clear pedestrian and driveway access planning"];
const commercialFeatures = ["Main contractor and developer partner frameworks", "Occupied premises and high-street shopfronts", "Commercial refurbishment and structural fit-outs", "Full design calculations and TG20 compliance", "Handover certificates and routine 7-day inspections"];
const roofingTradeCards = [{
  title: "Roofing Scaffolding",
  description: "Independent tied access and loading bays for re-roofs and solar installations.",
  to: "/RoofingScaffoldingPage",
  icon: HardHat
}, {
  title: "Temporary Roofs",
  description: "Full weather-tight tin hats and shrink-wrap protection for ongoing building works.",
  to: "/TemporaryRoofsPage",
  icon: Umbrella
}, {
  title: "Edge Protection",
  description: "Safety handrails and toe boards to safeguard roofers and trades at height.",
  to: "/EdgeProtectionPage",
  icon: ShieldCheck
}];
const nearbyAreas = [{
  name: "Gerrards Cross",
  to: "/scaffolding-in-gerrards-cross"
}, {
  name: "Amersham",
  to: "/scaffolding-in-amersham"
}, {
  name: "Marlow",
  to: "/scaffolding-in-marlow"
}];
export default function BeaconsfieldCoverage() {
  return <section id="beaconsfieldscaffoldingcoverage" data-nav="light" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="outline" className="mb-3 gap-1.5 font-medium">
            <MapPin className="size-3.5 text-primary" />
            <span>HP9 & South Bucks Coverage</span>
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4">
            Scaffolding services across Beaconsfield
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            With HP9 established as our principal Beaconsfield district—spanning both Old Town and New Town—we provide TG20-compliant scaffolding with clear handovers and punctual scheduling for local commercial refurbishments, residential extensions, and roofing projects.
          </p>

          {/* Key coverage tags */}
          <div className="flex flex-wrap gap-2 mt-6">
            {coverageAreas.map((area, i) => <span key={area} data-index={i} className="inline-flex items-center gap-1.5 text-xs font-medium bg-muted text-foreground px-3 py-1 rounded-lg border border-border">
                <CheckCircle2 className="size-3 text-primary" />
                {area}
              </span>)}
          </div>
        </div>

        {/* Two main subsections: Residential & Commercial */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12 md:mb-16">
          {/* Subsection 1: Residential Scaffolding */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4
        }}>
            <Card className="h-full border-border bg-card shadow-sm flex flex-col justify-between">
              <div>
                <CardHeader>
                  <div className="size-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-3">
                    <Home className="size-6" />
                  </div>
                  <CardTitle className="text-2xl font-bold text-foreground">
                    Residential scaffolding in Beaconsfield
                  </CardTitle>
                  <CardDescription className="text-base text-muted-foreground">
                    Safe, tidy access solutions for homeowners, builders, and roofers across the HP9 district including Old Town, New Town, and surrounding residential roads.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-2.5">
                    {residentialFeatures.map((item, i) => <li key={item} data-index={i} className="flex items-start gap-2.5 text-sm text-foreground">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>)}
                  </ul>

                  <Separator className="my-6" />

                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Specialist Residential Pages
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button asChild variant="outline" size="sm">
                        <Link to="/ResidentialScaffoldingPage">
                          Residential Scaffolding
                          <ArrowRight className="size-3.5 ml-1" />
                        </Link>
                      </Button>
                      <Button asChild variant="outline" size="sm">
                        <Link to="/ChimneyScaffoldingPage">
                          Chimney Scaffolding
                          <ArrowRight className="size-3.5 ml-1" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </div>

              <div className="px-6 pb-6 pt-2">
                <Button asChild className="w-full">
                  <Link to="/ContactRequestQuotePage">
                    Request residential quote
                  </Link>
                </Button>
              </div>
            </Card>
          </motion.div>

          {/* Subsection 2: Commercial Scaffolding */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: 0.1
        }}>
            <Card className="h-full border-border bg-card shadow-sm flex flex-col justify-between">
              <div>
                <CardHeader>
                  <div className="size-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-3">
                    <Building2 className="size-6" />
                  </div>
                  <CardTitle className="text-2xl font-bold text-foreground">
                    Commercial scaffolding in Beaconsfield
                  </CardTitle>
                  <CardDescription className="text-base text-muted-foreground">
                    Engineered access for main contractors, local developers, retail premises, and occupied business developments across HP9 and South Bucks.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-2.5">
                    {commercialFeatures.map((item, i) => <li key={item} data-index={i} className="flex items-start gap-2.5 text-sm text-foreground">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>)}
                  </ul>

                  <Separator className="my-6" />

                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Commercial & Contractor Access
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button asChild variant="outline" size="sm">
                        <Link to="/CommercialScaffoldingPage">
                          Commercial Scaffolding
                          <ArrowRight className="size-3.5 ml-1" />
                        </Link>
                      </Button>
                      <Button asChild variant="outline" size="sm">
                        <Link to="/MainContractorsDevelopersPage">
                          Main Contractors & Developers
                          <ArrowRight className="size-3.5 ml-1" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </div>

              <div className="px-6 pb-6 pt-2">
                <Button asChild variant="secondary" className="w-full">
                  <Link to="/ContactRequestQuotePage">
                    Arrange commercial site visit
                  </Link>
                </Button>
              </div>
            </Card>
          </motion.div>
        </div>

        {/* Roofing & Trade Access Block */}
        <div className="rounded-2xl border border-border bg-muted/40 p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h3 className="text-xl md:text-2xl font-bold text-foreground">
                Roofing & Trade Access
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Specialist support structures designed for roofing specialists, solar contractors, and exterior trades.
              </p>
            </div>
            <Button asChild variant="outline" size="sm" className="self-start md:self-auto">
              <Link to="/ContactRequestQuotePage">
                Discuss access requirements
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {roofingTradeCards.map((service, i) => {
            const IconComp = service.icon;
            return <div key={service.title} data-index={i} className="rounded-xl border border-border bg-card p-5 shadow-xs flex flex-col justify-between">
                  <div>
                    <div className="size-9 rounded-md bg-accent text-accent-foreground flex items-center justify-center mb-3">
                      <IconComp className="size-5 text-primary" />
                    </div>
                    <h4 className="font-semibold text-foreground text-base mb-1">
                      {service.title}
                    </h4>
                    <p className="text-xs text-muted-foreground leading-relaxed mb-4">
                      {service.description}
                    </p>
                  </div>
                  <Link to={service.to} className="inline-flex items-center text-xs font-semibold text-primary hover:underline group">
                    View details
                    <ArrowRight className="size-3 ml-1 transition-transform group-hover:translate-x-0.5" />
                  </Link>
                </div>;
          })}
          </div>
        </div>

        {/* Nearby Areas Coverage Links */}
        <div className="mt-12 pt-8 border-t border-border flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h3 className="text-base font-semibold text-foreground">
              Nearby areas we cover
            </h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              Rapid response and scheduled scaffolding across South Buckinghamshire and adjacent districts.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {nearbyAreas.map((area, i) => <Button key={area.name} asChild variant="outline" size="sm" data-index={i}>
                <Link to={area.to}>
                  {area.name}
                  <ArrowRight className="size-3.5 ml-1" />
                </Link>
              </Button>)}
            <Button asChild variant="ghost" size="sm" className="text-primary hover:text-primary/90 font-medium">
              <Link to="#">
                View all areas
                <ArrowRight className="size-3.5 ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}